export interface TapPoint {
  x: number;
  y: number;
}

export interface DoubleTapTracker {
  begin(pointerId: number, point: TapPoint): void;
  move(pointerId: number, point: TapPoint): void;
  end(pointerId: number, point: TapPoint, canceled?: boolean): boolean;
  cancel(): void;
}

const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DISTANCE_PX = 20;
const TAP_MOVEMENT_PX = 8;

/**
 * Recognises two completed, stationary, single-pointer touch gestures.
 * A pan, cancellation, or second active pointer invalidates both the current
 * gesture and any completed first-tap candidate.
 */
export function createDoubleTapTracker(
  now: () => number = () => performance.now(),
): DoubleTapTracker {
  const active = new Map<number, { start: TapPoint; latest: TapPoint; eligible: boolean }>();
  let previousTap: { at: number; point: TapPoint } | null = null;

  function invalidateActive(): void {
    for (const touch of active.values()) touch.eligible = false;
    previousTap = null;
  }

  return {
    begin(pointerId, point): void {
      const singlePointer = active.size === 0;
      if (!singlePointer) invalidateActive();
      active.set(pointerId, {
        start: { ...point },
        latest: { ...point },
        eligible: singlePointer,
      });
    },

    move(pointerId, point): void {
      const touch = active.get(pointerId);
      if (!touch) return;
      touch.latest = { ...point };
      if (Math.hypot(point.x - touch.start.x, point.y - touch.start.y) > TAP_MOVEMENT_PX) {
        touch.eligible = false;
        previousTap = null;
      }
    },

    end(pointerId, point, canceled = false): boolean {
      const touch = active.get(pointerId);
      if (!touch) return false;
      touch.latest = { ...point };
      const wasOnlyPointer = active.size === 1;
      active.delete(pointerId);

      const moved = Math.hypot(point.x - touch.start.x, point.y - touch.start.y);
      if (canceled || !wasOnlyPointer || !touch.eligible || moved > TAP_MOVEMENT_PX) {
        previousTap = null;
        return false;
      }

      const endedAt = now();
      const isDoubleTap =
        previousTap !== null &&
        endedAt - previousTap.at < DOUBLE_TAP_MS &&
        Math.hypot(point.x - previousTap.point.x, point.y - previousTap.point.y) <
          DOUBLE_TAP_DISTANCE_PX;
      if (isDoubleTap) {
        previousTap = null;
        return true;
      }

      previousTap = { at: endedAt, point: { ...point } };
      return false;
    },

    cancel(): void {
      invalidateActive();
    },
  };
}
